/**
 * Which of ABB's end customers a Rexel sold-to customer is, by name.
 *
 * ABB's SPA list numbers end customers with ABB's own customer numbers, and
 * nothing in either file carries both; the only bridge is the name, written
 * differently on each side ("MAD CAT ELECTRIC CO INC" / "MAD-CAT ELECTRIC CO
 * INC.", "JESSE LEE - ELECTRICIAN" / "JESSE LEE ELECTRIC"). So a match comes
 * with how sure it is, and the report shows that beside it:
 *
 * - `exact`: the same words once punctuation is gone.
 * - `strong`: the same distinctive words once trade words and legal suffixes
 *   are gone ("LEAHY ELECTRICAL SERVICES INC" / "LEAHY ELECTRIC").
 * - `possible`: the end customer's distinctive words are all in the
 *   customer's name, which adds at most two ("BERNARD WALSH" / "WALSH
 *   ELECTRIC"); or the other way round when ABB's name adds only trade words
 *   ("STEVENSON ELECTRIC" / "STEVENSON PLUMBING & ELECTRIC"). ABB adding a
 *   surname is another company ("BARRY ELECTRICAL" is not "BARRY LATREILLE
 *   ELECTRIC"). A person checks these; a shared generic word ("NORTH",
 *   "INDUSTRIAL") alone never counts.
 */

export type NameMatchLevel = 'exact' | 'strong' | 'possible';

/** Trade words and legal suffixes: they say nothing about which company it is. */
const STOP_WORDS = new Set([
  'AND',
  'CO',
  'COMPANY',
  'CONT',
  'CONTR',
  'CONTRACTING',
  'CONTRACTORS',
  'CORP',
  'CORPORATION',
  'ELEC',
  'ELECT',
  'ELECTL',
  'ELECTRIC',
  'ELECTRICAL',
  'ELECTRICIAN',
  'ELECTRIQUE',
  'INC',
  'LIMITED',
  'LLC',
  'LTD',
  'LTEE',
  'SERV',
  'SERVICE',
  'SERVICES',
  'THE',
]);

/** A province or region tag ABB appends ("CARON ELECTRIC - ON"), dropped when it ends a longer name. */
const REGION_TAGS = new Set([
  'AB',
  'ATL',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'ON',
  'PE',
  'QC',
  'SK',
]);

/** Words many unrelated companies share; matching on one of these alone is noise. */
const GENERIC_WORDS = new Set([
  'CANADA',
  'CANADIAN',
  'CENTRE',
  'COLLEGE',
  'COMMUNICATIONS',
  'CONSTRUCTION',
  'DISTRICT',
  'EAST',
  'ENERGY',
  'GENERATION',
  'GROUP',
  'HOME',
  'INDUSTRIAL',
  'MECHANICAL',
  'NORTH',
  'ONTARIO',
  'POWER',
  'PRECISION',
  'RENEWABLE',
  'ROYAL',
  'SCHOOL',
  'SOLAR',
  'SOLUTIONS',
  'SOUTH',
  'SYSTEMS',
  'TECH',
  'TRADE',
  'WEST',
]);

/** What a company does, not who it is: ABB's name may add these to a customer's. */
const TRADE_WORDS = new Set([
  'AUTOMATION',
  'CABLING',
  'CONSTRUCTION',
  'CONTROLS',
  'DATA',
  'ENTERPRISES',
  'HEATING',
  'HVAC',
  'INDUSTRIES',
  'LIGHTING',
  'MAINTENANCE',
  'MECHANICAL',
  'PLUMBING',
  'SECURITY',
  'SYSTEMS',
  'TECHNOLOGIES',
]);

/**
 * Upper-case words with punctuation gone; runs of single letters are joined,
 * so "H B ELECTRIC" and "J. R. CARON" read as "HB" and "JR CARON".
 */
export function nameWords(name: string): string[] {
  const words = name
    .toUpperCase()
    .replace(/&/g, ' AND ')
    .replace(/['’]/g, '')
    .split(/[^A-Z0-9]+/)
    .filter(Boolean);
  const joined: string[] = [];
  let initials = false;
  for (const word of words) {
    const isInitial = /^[A-Z]$/.test(word);
    if (isInitial && initials) {
      joined[joined.length - 1] += word;
      continue;
    }
    joined.push(word);
    initials = isInitial;
  }
  return joined;
}

/** The words that say which company it is. */
export function distinctiveWords(name: string): string[] {
  const words = nameWords(name).filter((word) => !STOP_WORDS.has(word));
  const last = words.at(-1);
  if (words.length > 1 && last !== undefined && REGION_TAGS.has(last)) {
    words.pop();
  }
  return words;
}

function isSubset(small: readonly string[], large: readonly string[]) {
  const words = new Set(large);
  return small.every((word) => words.has(word));
}

/**
 * How sure it is that a customer's name and an ABB end customer's name are
 * one company, or null when they are not. Not symmetric: see `possible`.
 */
export function matchNames(
  customer: string,
  endCustomer: string,
): NameMatchLevel | null {
  if (nameWords(customer).join(' ') === nameWords(endCustomer).join(' ')) {
    return 'exact';
  }
  const ours = distinctiveWords(customer);
  const theirs = distinctiveWords(endCustomer);
  if (ours.length === 0 || theirs.length === 0) return null;
  if (ours.join(' ') === theirs.join(' ')) return 'strong';
  const ourWords = new Set(ours);
  const theirWords = new Set(theirs);
  const shared = ours.filter((word) => theirWords.has(word));
  const telling = shared.some(
    (word) => word.length >= 4 && !GENERIC_WORDS.has(word),
  );
  if (!telling) return null;
  if (isSubset(theirs, ours) && ours.length - theirs.length <= 2) {
    return 'possible';
  }
  const added = theirs.filter((word) => !ourWords.has(word));
  if (isSubset(ours, theirs) && added.every((word) => TRADE_WORDS.has(word))) {
    return 'possible';
  }
  return null;
}

const LEVEL_RANK: Record<NameMatchLevel, number> = {
  exact: 0,
  strong: 1,
  possible: 2,
};

export interface NamedParty {
  /** Unique within its side. */
  id: string;
  name: string;
}

export interface NameMatch {
  customerId: string;
  candidateId: string;
  level: NameMatchLevel;
}

/**
 * Every customer's matches among the candidates, surest first. A possible
 * match stays beside a sure one (ABB may hold the same company under two
 * numbers: "J.R CARON ELECTRIC" and "CARON ELECTRIC - ON"), but is dropped
 * when that candidate is an exact or strong match of another customer on the
 * list ("NORTH ELECTRIC" is not "TRUE NORTH ELECTRICAL" when True North is
 * on the list too).
 */
export function matchCustomers(
  customers: readonly NamedParty[],
  candidates: readonly NamedParty[],
): NameMatch[] {
  const all: NameMatch[] = [];
  for (const customer of customers) {
    for (const candidate of candidates) {
      const level = matchNames(customer.name, candidate.name);
      if (level) {
        all.push({
          customerId: customer.id,
          candidateId: candidate.id,
          level,
        });
      }
    }
  }
  const claimed = new Set<string>();
  for (const match of all) {
    if (match.level !== 'possible') claimed.add(match.candidateId);
  }
  return all
    .filter(
      (match) => match.level !== 'possible' || !claimed.has(match.candidateId),
    )
    .sort((x, y) => LEVEL_RANK[x.level] - LEVEL_RANK[y.level]);
}
